'use client';

import Image from '@/components/Image';
import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useInViewportAnimation } from '@/hooks/useInViewportAnimation';
import { EPlatform } from '@/utils/enums/platform-enum';

// BlueLightEffects 组件接口定义
interface BlueLightEffectsProps {
  effectData: {
    title?: string;
    description?: string;
    background_shadow?: string;
    middle_arr?: Array<{
      img: string;
      title: string;
      description: string;
    }>;
    bottom_arr?: {
      title: string;
      words: string[];
      image1: string;
      image2: string;
      background_shadow: string;
    };
  };
  deviceType?: EPlatform;
  isFromCache?: boolean;
  onSectionExp?: (params: any) => void;
}

export default function BlueLightEffects({
  effectData,
  deviceType,
  isFromCache = false,
  onSectionExp,
}: BlueLightEffectsProps) {
  const isMobile = deviceType !== EPlatform.WEB;

  // 使用 useInView 进行模块曝光检测
  const [expRef, inView] = useInView({
    threshold: 0.5, // 50%可见时触发
    triggerOnce: true, // 只触发一次
  });

  // isFromCache 现在通过参数传入，无需检测

  // 顶部标题动画 - 只在PC端执行
  const topTitleRef = useInViewportAnimation(
    () => {
      if (isFromCache || isMobile) return;

      try {
        const elements = ['effect-top-title-h2', 'effect-top-title-p'];
        elements.forEach((className) => {
          const dom = document.getElementsByClassName(className);
          if (dom[0]) {
            dom[0].classList.add('activityFadeInUpAnimate');
          }
        });
      } catch {
        // 出错时直接显示
        const elements = document.querySelectorAll('.effect-top-title .opacity0');
        elements.forEach((el) => {
          (el as HTMLElement).style.opacity = '1';
        });
      }
    },
    { threshold: 0.5, once: true },
  );

  // 中间部分动画 - 只在PC端执行
  const middlePartRef = useInViewportAnimation(
    () => {
      if (isFromCache || isMobile) return;

      try {
        const elements = [
          'effect-middle-part-img',
          'effect-middle-part-h3',
          'effect-middle-part-p',
        ];
        elements.forEach((className) => {
          const domList = document.getElementsByClassName(className);
          Array.from(domList).forEach((el) => {
            el.classList.add('activityFadeInUpAnimate');
          });
        });
      } catch {
        // 出错时直接显示
        const elements = document.querySelectorAll('.blue-light-effect-middle-part .opacity0');
        elements.forEach((el) => {
          (el as HTMLElement).style.opacity = '1';
        });
      }
    },
    { threshold: 0.5, once: true },
  );

  // 底部部分动画 - 只在PC端执行
  const bottomPartRef = useInViewportAnimation(
    () => {
      if (isFromCache || isMobile) return;

      try {
        // 文字元素动画
        const textElements = ['effect-bottom-part-h2', 'effect-bottom-part-p'];
        textElements.forEach((className) => {
          const domList = document.getElementsByClassName(className);
          Array.from(domList).forEach((el) => {
            el.classList.add('activityFadeInUpAnimate');
          });
        });

        // 图片动画
        const img1 = document.getElementsByClassName('effect-bottom-part-one');
        const img2 = document.getElementsByClassName('effect-bottom-part-two');
        if (img1[0]) {
          img1[0].classList.add('activityFadeInLeftAnimate');
        }
        if (img2[0]) {
          img2[0].classList.add('activityFadeInRightAnimate');
        }
      } catch {
        // 出错时直接显示
        const elements = document.querySelectorAll('.blue-light-effect-bottom-part .opacity0');
        elements.forEach((el) => {
          (el as HTMLElement).style.opacity = '1';
        });
      }
    },
    { threshold: 0.5, once: true },
  );

  // 模块曝光埋点处理
  useEffect(() => {
    if (inView && onSectionExp) {
      onSectionExp({
        mkt_name: '防蓝光的影响',
      });
    }
  }, [inView, onSectionExp]);

  if (!effectData) return null;

  return (
    <div className="effect-box">
      {/* 顶部标题区域 */}
      <div
        ref={isMobile ? null : (topTitleRef as React.RefObject<HTMLDivElement>)}
        className="top-title blue-light-effect-top-title border-b border-[#3e4758]"
        style={{
          backgroundImage: effectData.background_shadow
            ? `url(${effectData.background_shadow})`
            : undefined,
          backgroundRepeat: 'no-repeat',
          backgroundSize: '100% auto',
        }}
      >
        <div
          className={`mx-auto ${
            isMobile ? 'py-[32px] px-[24px] w-auto text-left' : 'py-[4.79vw] w-[52vw] text-center'
          }`}
        >
          <h2
            className={`font-bs text-white effect-top-title-h2 ${
              isMobile
                ? 'text-[20px] leading-[28px]'
                : 'text-[20px] leading-[28px] xl:text-[2.08vw] xl:leading-[3.125vw] opacity0'
            }`}
            style={{
              background: 'linear-gradient(90deg, #FFFFFF 0%, #A1BCFF 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
            dangerouslySetInnerHTML={{ __html: effectData.title || '' }}
          />
          <p
            className={`text-[#b7b7b7] effect-top-title-p ${
              isMobile
                ? 'mt-[8px] text-[11px] leading-[18px]'
                : 'mt-[16px] text-[14px] leading-[26px] opacity0'
            }`}
          >
            {effectData.description}
          </p>
        </div>
      </div>

      {/* 中间影响展示区域 */}
      {effectData.middle_arr && (
        <ul
          ref={(node) => {
            // 为埋点设置ref
            expRef(node);
            // 为动画设置ref（仅PC端）
            if (!isMobile && middlePartRef && typeof middlePartRef !== 'function') {
              (middlePartRef as React.MutableRefObject<HTMLUListElement | null>).current = node;
            }
          }}
          className={`middle-part blue-light-effect-middle-part border-b border-[#3e4758] ${
            isMobile ? 'flex flex-col' : 'flex justify-between items-center'
          }`}
        >
          {effectData.middle_arr.map((item, index) => (
            <li
              key={index}
              className={`flex flex-col box-border border-[#3e4758] ${
                isMobile
                  ? 'w-full pt-[32px] pb-[28px] px-[24px] border-b last:border-b-0 border-r-0'
                  : 'w-1/2 py-[4.16vw] px-[6.25vw] border-r last:border-r-0'
              }`}
            >
              <Image
                src={item.img}
                alt={item.title}
                width={800}
                height={600}
                className={`w-full h-auto effect-middle-part-img ${isMobile ? '' : 'opacity0'}`}
              />
              <h3
                className={`font-bs text-white effect-middle-part-h3 ${
                  isMobile
                    ? 'mt-[24px] mb-[8px] text-[16px] leading-[24px]'
                    : 'mt-[2.3vw] mb-[1.04vw] text-[16px] leading-[24px] xl:text-[1.67vw] xl:leading-[1.66vw] opacity0'
                }`}
              >
                {item.title}
              </h3>
              <p
                className={`text-[#b7b7b7] text-left effect-middle-part-p ${
                  isMobile
                    ? 'text-[11px] leading-[18px]'
                    : 'text-[11px] leading-[18px] xl:text-[0.73vw] xl:leading-[1.35vw] opacity0'
                }`}
              >
                {item.description}
              </p>
            </li>
          ))}
        </ul>
      )}

      {/* 底部对比区域 */}
      {effectData.bottom_arr && (
        <div
          ref={isMobile ? null : (bottomPartRef as React.RefObject<HTMLDivElement>)}
          className={`bottom-part blue-light-effect-bottom-part relative border-b border-[#3e4758] ${
            isMobile ? 'flex flex-col' : 'flex'
          }`}
          style={{
            backgroundImage: effectData.bottom_arr.background_shadow
              ? `url(${effectData.bottom_arr.background_shadow})`
              : undefined,
            backgroundRepeat: 'no-repeat',
            backgroundSize: isMobile ? '100% auto' : 'auto 100%',
          }}
        >
          {/* PC端：图片在右侧，WAP端：图片在上方 */}
          {isMobile ? (
            <>
              {/* 移动端：图片区域在上 */}
              <div className="img-wrap h-full relative flex justify-end">
                <Image
                  src={effectData.bottom_arr.image1}
                  alt=""
                  width={400}
                  height={300}
                  className="img-one effect-bottom-part-one fadeInLeft z-[2] h-auto absolute right-[49.067vw] top-[6.133vw] w-[50.933vw]"
                />
                <Image
                  src={effectData.bottom_arr.image2}
                  alt=""
                  width={400}
                  height={300}
                  className="img-two effect-bottom-part-two w-[60.8vw] h-auto"
                />
              </div>

              {/* 移动端：文字区域在下 */}
              <div className="left-part box-border w-full py-[28px] px-[24px]">
                <h2
                  className="font-bs text-white effect-bottom-part-h2 text-[16px] leading-[24px]"
                  style={{
                    background: 'linear-gradient(90deg, #FFFFFF 0%, #A1BCFF 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {effectData.bottom_arr.title}
                </h2>
                {effectData.bottom_arr.words?.map((word, index) => (
                  <p
                    key={index}
                    className="text-[#b7b7b7] effect-bottom-part-p mt-[8px] text-[11px] leading-[18px]"
                  >
                    {word}
                  </p>
                ))}
              </div>
            </>
          ) : (
            <>
              {/* PC端：文字区域在左 */}
              <div className="left-part box-border w-[39.896vw] py-[28px] px-[24px] xl:py-[3.69vw] xl:px-[3.69vw]">
                <h2
                  className="font-bs text-white effect-bottom-part-h2 text-[16px] leading-[24px] xl:text-[2.08vw] xl:leading-[3.125vw] opacity0"
                  style={{
                    background: 'linear-gradient(90deg, #FFFFFF 0%, #A1BCFF 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {effectData.bottom_arr.title}
                </h2>
                {effectData.bottom_arr.words?.map((word, index) => (
                  <p
                    key={index}
                    className="text-[#b7b7b7] effect-bottom-part-p mt-[8px] text-[11px] leading-[18px] xl:text-[0.73vw] xl:leading-[1.35vw] xl:mt-[0.83vw] opacity0"
                  >
                    {word}
                  </p>
                ))}
              </div>

              {/* PC端：图片区域在右 */}
              <div className="img-wrap h-full absolute right-0 top-0">
                <Image
                  src={effectData.bottom_arr.image2}
                  alt=""
                  width={700}
                  height={500}
                  className="img-two effect-bottom-part-two w-auto h-full opacity0"
                />
                <Image
                  src={effectData.bottom_arr.image1}
                  alt=""
                  width={700}
                  height={500}
                  className="img-one effect-bottom-part-one fadeInLeft absolute right-[31vw] bottom-[2.083vw] w-[36.979vw] h-auto opacity0 z-[2]"
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
